import { equals } from 'ramda'

import { HARVESTING_FLOW } from '~/src/Flow/constants'
import { EMPTY_ARRAY, EMPTY_OBJECT } from '~/src/Lib/Utils'

export const isHarvestingUrl = routeInfo => routeInfo?.pattern && routeInfo.pattern === `/${HARVESTING_FLOW}/:id`

export const formLatestHarvestingData = ({ harvest }) => {
  const sources = harvest.cultivars

  return {
    sources,
    harvestId: harvest.id,
  }
}

export const getGenericQueuedSources = (queue, settings) => {
  if (settings.weighingIndividually) {
    return queue.reduce((qs, queued) => {
      qs[queued.cultivar] ??= 0
      qs[queued.cultivar] += 1
      if (!queued.uid && !queued.id) return qs
      qs[queued.uid || queued.id] ??= 0
      qs[queued.uid || queued.id] += 1
      return qs
    }, {})
  }
  return queue.reduce((qs, queued) => {
    qs[queued.cultivar] ??= 0
    qs[queued.cultivar] += 1
    return qs
  }, {})
}

export const getNewQueue = ({
  addedAt = new Date().toISOString(),
  modifiedAt = addedAt,
  weight: rawWeight,
  version = 0,
  ...entryToAdd
}, queue) => {
  let weight = rawWeight
  if (typeof weight !== 'number' || Number.isNaN(weight)) {
    if (typeof weight === 'string') {
      weight = Number(weight)
    }
    if (Number.isNaN(weight)) {
      weight = 0
    }
  }
  const indexInQueue = queue.findIndex(elem => elem.id === entryToAdd.id)

  const entry = {
    ...entryToAdd,
    addedAt,
    modifiedAt,
    version: version + 1,
    isInQueue: true,
    weight
  }
  // if we've found an index - we need to update an exisitng item in the queue. Otherwise, we need to add a new item at the beginning of the queue
  if (indexInQueue > -1) {
    const newQueue = [...queue]
    entry.modifiedAt = new Date().toISOString()
    newQueue[indexInQueue] = entry
    return newQueue
  }
  return [entry, ...queue]
}

const RECONCILERS = {
  GENERIC: (state, flow) => {
    const {
      processedRooms: flowProcessedRooms = EMPTY_OBJECT,
    } = ('data' in flow ? flow.data : flow)
    const {
      processedRooms: localProcessedRooms = null,
      queue,
      settings,
    } = state

    return {
      ...state,
      processedRooms: localProcessedRooms ?? flowProcessedRooms,
      queuedSources: getGenericQueuedSources(queue, settings),
    }
  },
  SHARED: (state, flow) => {
    const {
      dequeued: flowDequeued = EMPTY_ARRAY,
      queue: flowQueue = EMPTY_ARRAY,
    } = ('data' in flow ? flow.data : flow)
    const {
      dequeued: localDequeued = EMPTY_ARRAY,
      queue: localQueue = EMPTY_ARRAY,
    } = state

    // merge the current flow with the current harvesting state
    const dequeuedSet = new Set([...flowDequeued, ...localDequeued])
    const [localFilteredQueue, lfqIDs] = localQueue.reduce((lfq, item) => {
      const { id } = item
      if (dequeuedSet.has(id)) return lfq
      const [queue, ids] = lfq
      queue.push(item)
      ids.add(id)
      return lfq
    }, [[], new Set()])
    const dequeued = [...dequeuedSet]
    const queue = [
      ...localFilteredQueue,
      ...flowQueue.filter(({ id }) => !lfqIDs.has(id) && !dequeuedSet.has(id))
    ]

    return { ...state, dequeued, queue }
  },
}

export const reconcileStateAndFlow = (state, flow, which) => (
  [RECONCILERS.SHARED, RECONCILERS[which]].reduce((nextState, reconciler) => reconciler(nextState, flow), state)
)

const textSorter = (a, b) => a.localeCompare(b)
export const isOutOfSync = (state, flow) => {
  const { dequeued: stateDequeued, queue: stateQueue } = state
  if (!flow.data) return true
  const { dequeued: flowDequeued, queue: flowQueue } = flow.data

  const dequeuedDiff = !flowDequeued
    || stateDequeued.length !== flowDequeued.length
    || !equals(stateDequeued.slice().sort(textSorter), flowDequeued.slice().sort(textSorter))

  if (dequeuedDiff) {
    return dequeuedDiff
  }
  const queueDiffLength = stateQueue.length !== flowQueue.length
  if (queueDiffLength) {
    return queueDiffLength
  }

  const fqSet = new Set(flowQueue.map(({ id }) => id))
  const stateQueueHasNewItems = stateQueue.some(({ id }) => !fqSet.has(id))

  return stateQueueHasNewItems
}
